import type { ReactNode, Ref } from 'react'
import type { Id } from '@/infra/id'
import cx from 'classnames'
import { format } from 'date-fns'
import {
  EmptyState,
  EmptyStateDescription,
  EmptyStateIllustration,
  EmptyStateTitle,
} from '@/components/EmptyState'
import { SearchField } from '@/components/SearchField'
import { Select } from '@/components/inputs/Select'
import { RawButton } from '@/components/Button'
import { Checkbox } from '@/components/Checkbox'
import { Icon } from '@/components/Icon'
import { Skeleton, useSkeletonListContext } from '@/components/Skeleton'
import {
  type DocumentActions,
  DocumentItemActionsDropdown,
} from '@/features/documents/components/DocumentItemActionsDropdown'
import styles from './JobDocumentsSection.module.css'
import DocumentsDisabledIllustration from '@/components/SpotIllustration/DocumentsDisabledIllustration'
import DragAndUploadIllustration from '@/components/SpotIllustration/DragAndUploadIllustration'

export interface JobDocumentsSectionDocument {
  id: Id
  title: string
  displayTitle: string
  typeName: string
  isProcessing: boolean
  ownerName: string
  dateCreated: Date
  selected: boolean
  onToggleSelected: () => unknown
  onPress: () => unknown
  /**
   * `null` hides the actions dropdown.
   */
  actions: DocumentActions | null
}

export interface JobDocumentsSectionType {
  id: string
  name: string
}

export interface JobDocumentsSectionProps {
  hasDocuments: boolean
  /**
   * Whether the documents are loading with none to show yet.
   */
  isSkeleton: boolean
  /**
   * The documents left after the filters.
   */
  documents: JobDocumentsSectionDocument[]
  /**
   * The document types the table can be narrowed to.
   */
  types: JobDocumentsSectionType[]
  /**
   * ID of the type the table is narrowed to, or empty for all types.
   */
  typeFilter: string
  onTypeFilterChange: (typeId: string) => unknown
  query: string
  onQueryChange: (query: string) => unknown
  allSelected: boolean
  onToggleAll: () => unknown
  documentCount: number
  selectedCount: number
  canViewDocs: boolean
  canRemoveDocs: boolean
  isClosed: boolean
  corporateMode: boolean
  isDownloadSelectedPending: boolean
  onClearSelection: () => unknown
  onRemoveSelected: () => unknown
  onDownloadSelected: () => unknown
  txfButton?: ReactNode
  actionsDropdown?: ReactNode
  /**
   * Loads more documents, holding their place with
   * {@link FileRowSkeleton}s, including while the first ones load.
   */
  scrollLoader?: ReactNode
  dialogs?: ReactNode
}

/**
 * Every file on the job in one table, newest first, filterable by document
 * type and by name.
 */
export function JobDocumentsSection({
  hasDocuments,
  isSkeleton,
  documents,
  types,
  typeFilter,
  onTypeFilterChange,
  query,
  onQueryChange,
  allSelected,
  onToggleAll,
  documentCount,
  selectedCount,
  canViewDocs,
  canRemoveDocs,
  isClosed,
  corporateMode,
  isDownloadSelectedPending,
  onClearSelection,
  onRemoveSelected,
  onDownloadSelected,
  txfButton,
  actionsDropdown,
  scrollLoader,
  dialogs,
}: JobDocumentsSectionProps) {
  const isLoading = canViewDocs && isSkeleton

  return (
    <>
      {selectedCount > 0 ? (
        <div className={cx(styles.bar, styles.bulk)}>
          <span className={styles.bulkCount}>{selectedCount} selected</span>
          <RawButton className={styles.bulkAction} onPress={onClearSelection}>
            Clear
          </RawButton>
          {canRemoveDocs && (
            <RawButton className={styles.bulkAction} onPress={onRemoveSelected}>
              Remove
            </RawButton>
          )}
          <RawButton
            className={cx(styles.bulkAction, styles.bulkPrimary)}
            isDisabled={isDownloadSelectedPending}
            onPress={onDownloadSelected}
          >
            <Icon name="download" size={14} aria-hidden />
            Download
          </RawButton>
        </div>
      ) : (
        <div className={cx(styles.bar, styles.toolbar)}>
          {hasDocuments && types.length > 1 && (
            <div className={styles.typeFilter}>
              <Select
                size="small"
                value={typeFilter}
                onChange={(e) => onTypeFilterChange(e.target.value)}
                aria-label="Filter by document type"
              >
                <option value="">All types</option>
                {types.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </Select>
            </div>
          )}
          {hasDocuments && (
            <div className={styles.filter}>
              <SearchField
                size="small"
                stretch
                value={query}
                onChange={onQueryChange}
                placeholder="Filter documents"
                aria-label="Filter documents"
                testId="job-documents-filter"
              />
            </div>
          )}
          <span className={styles.spacer} />
          {txfButton}
          {actionsDropdown}
        </div>
      )}

      <div data-test="job-documents-section">
        {(hasDocuments || isLoading) && (
          <div className={cx(styles.grid, styles.head)}>
            <Checkbox
              checked={allSelected}
              aria-label="Select all shown"
              onChange={onToggleAll}
              size={14}
            />
            <span>Name</span>
            <span>Document type</span>
            <span>Uploaded by</span>
            <span className={styles.right}>Date</span>
            <span />
          </div>
        )}

        {documents.map((d) => (
          <FileRow key={d.id} document={d} />
        ))}

        {canViewDocs && scrollLoader}

        {hasDocuments && documents.length === 0 && (
          <div className={styles.empty}>No documents match these filters.</div>
        )}

        {canViewDocs && !isSkeleton && !hasDocuments && (
          <div className={styles.emptyState}>
            {isClosed ? (
              <EmptyState size="sm">
                {!corporateMode && (
                  <EmptyStateIllustration
                    illustration={DocumentsDisabledIllustration}
                  />
                )}
                <EmptyStateTitle>No documents</EmptyStateTitle>
                <EmptyStateDescription>
                  This job is closed, so no more documents can be added.
                </EmptyStateDescription>
              </EmptyState>
            ) : (
              <EmptyState size="sm">
                {!corporateMode && (
                  <EmptyStateIllustration
                    illustration={DragAndUploadIllustration}
                  />
                )}
                <EmptyStateTitle>No documents yet</EmptyStateTitle>
                <EmptyStateDescription>
                  Drop files anywhere on this page to upload them.
                </EmptyStateDescription>
              </EmptyState>
            )}
          </div>
        )}

        {hasDocuments && (
          <div className={styles.footer}>
            Showing {documents.length} of {documentCount}
          </div>
        )}
      </div>
      {dialogs}
    </>
  )
}

/**
 * A placeholder for a document row still loading. Render it inside a
 * `SkeletonList` or `SkeletonListInfiniteScrollTrigger`.
 */
export function FileRowSkeleton() {
  const { varyPercent, ref } = useSkeletonListContext()
  return (
    <div
      ref={ref as Ref<HTMLDivElement> | undefined}
      className={cx(styles.grid, styles.skeletonRow)}
      aria-hidden
    >
      <span />
      <Skeleton width={varyPercent(55)} />
      <span className={styles.type}>
        <Skeleton width={varyPercent(60)} />
      </span>
      <span className={styles.owner}>
        <Skeleton width={varyPercent(70)} />
      </span>
      <span className={cx(styles.date, styles.right)}>
        <Skeleton width={36} />
      </span>
      <span />
    </div>
  )
}

interface FileRowProps {
  document: JobDocumentsSectionDocument
}

function FileRow({ document }: FileRowProps) {
  return (
    <div
      className={cx(
        styles.grid,
        styles.row,
        document.selected && styles.selected,
      )}
      data-test={`document-list-item-${document.title}`}
    >
      <Checkbox
        checked={document.selected}
        aria-label={`Select ${document.title}`}
        onChange={document.onToggleSelected}
        size={14}
      />
      <RawButton className={styles.name} onPress={document.onPress}>
        {document.displayTitle}
      </RawButton>
      <span className={styles.type}>
        {document.isProcessing ? 'Processing…' : document.typeName}
      </span>
      <span className={styles.owner}>{document.ownerName}</span>
      <span className={cx(styles.date, styles.right)}>
        {format(document.dateCreated, 'MMM d')}
      </span>
      <span className={styles.rowAction}>
        {document.actions && (
          <DocumentItemActionsDropdown
            actions={document.actions}
            variant="ghost"
            title={document.title}
          />
        )}
      </span>
    </div>
  )
}
